// frontend/src/registry/componentRegistry.tsx
import React from 'react';
import { RegistryEntry } from '@/types/component';
import { z } from 'zod';
// import DateOfBirth from "@/components/los/DateOfBirth/DateOfBirth";
// import GenderSelection from "@/components/los/GenderSelection/GenderSelection";
import CreditScoreCheck from "@/components/LOS/CreditScoreCheck/CreditScoreCheck";
import HHIConsentCheckbox from "@/apps/los/components/HHIConsentCheckbox/HHIConsentCheckbox";
import JourneyAnalysis from "@/components/JourneyAnalysis.tsx";
import MaskedMobileInput from "@/components/MaskedMobileInput.tsx";
import MobileGuard from "@/components/MobileGuard.tsx";
import JourneyHeader from "@/components/JourneyHeader.tsx";
import BrandedJourneyHeader from "@/components/BrandedJourneyHeader.tsx";
import UserDetailModal from "@/components/UserDetailModal.tsx";
import AdminRoute from "@/components/AdminRoute.tsx";
import JourneySync from "@/components/JourneySync.tsx";
import Header from "@/components/Header.tsx";
import ScrollToTop from "@/components/ScrollToTop.tsx";
import LoginForm from "@/components/LoginForm.tsx";
import Row from "@/components/layout/Row.tsx";
import Grid from "@/components/layout/Grid.tsx";
import LoanOfferCard from "@/components/LoanOfferCard.tsx";
import AdminAnalytics from "@/components/AdminAnalytics.tsx";
import FullScreenLoader from "@/components/FullScreenLoader.tsx";
import CsvColumnMapper from "@/components/CsvColumnMapper.tsx";
import DataTableWithSlider from "@/components/DataTableWithSlider.tsx";
import ProtectedRoute from "@/components/ProtectedRoute.tsx";
import {
  CompositePersonalCommunicationAddress,
  CompositePersonalResidentialAddress,
  CompositeBusinessAddress,
  CompositeBusinessRegisteredAddress,
  IndividualAadhaarInput,
  PhoneNumberInput,
  IndividualPANInput,
  BusinessVintageInput,
  UdhyamRegistrationInput,
  LoanAmountInput,
  LoanTenureInput,
  LoanPurposeInput,
  ReasonForLoanInput,
  OtherBusinessesInput,
  LoanAmountSliderInput,
  LoanTenureDropdownInput,
  RepaymentFrequencyInput,
  SmartLoanTenureInput,
  ViewKFSButton,
  OfferCongratulationsMessage,
  UPIMandate,
  NACHMandate,
  LenderOfferSelection
} from "@/apps/los/components";
import BankAccountNumberInput from "@/apps/los/components/BankAccountNumberInput/BankAccountNumberInput.tsx";
import IFSCCodeInput from "@/apps/los/components/IFSCCodeInput/IFSCCodeInput.tsx";
import AccountTypeInput from "@/apps/los/components/AccountTypeInput/AccountTypeInput.tsx";
import BankConsentsInput from "@/apps/los/components/BankConsentsInput/BankConsentsInput.tsx";
import RepaymentMethodInput from "@/apps/los/components/RepaymentMethodInput/RepaymentMethodInput.tsx";
import CompositeAddress from "@/apps/los/components/CompositeAddress/CompositeAddress.tsx";
import IndividualNameInput from "@/apps/los/components/IndividualNameInput/IndividualNameInput.tsx";
import EmailInput from "@/apps/los/components/EmailInput/EmailInput.tsx";
import BusinessActivityInput from "@/apps/los/components/BusinessActivityInput/BusinessActivityInput.tsx";
import BusinessConstitutionInput from "@/apps/los/components/BusinessConstitutionInput/BusinessConstitutionInput.tsx";
import BusinessNameInput from "@/apps/los/components/BusinessNameInput/BusinessNameInput.tsx";
import CompanyPANInput from "@/apps/los/components/CompanyPANInput/CompanyPANInput.tsx";
import DateOfIncorporationInput from "@/apps/los/components/DateOfIncorporationInput/DateOfIncorporationInput.tsx";
import GSTNumberInput from "@/apps/los/components/GSTNumberInput/GSTNumberInput.tsx";
import AvgMonthlyBusinessIncomeInput from "@/apps/los/components/AvgMonthlyBusinessIncomeInput/AvgMonthlyBusinessIncomeInput.tsx";
import NatureOfBusinessInput from "@/apps/los/components/NatureOfBusinessInput/NatureOfBusinessInput.tsx";
import BankStatementCollection from "@/apps/los/components/BankStatementCollection/BankStatementCollection.tsx";
import BusinessAddressDetails from "@/apps/los/components/BusinessAddressDetails/BusinessAddressDetails.tsx";
import PersonalAddressDetails from "@/apps/los/components/PersonalAddressDetails/PersonalAddressDetails.tsx";
import LoanOfferWidget from "@/apps/los/components/LoanOfferWidget/LoanOfferWidget.tsx";
import NiyoginLoanOfferWidget from "@/apps/los/components/NiyoginLoanOfferWidget/NiyoginLoanOfferWidget.tsx";
import LoanDetails from "@/apps/los/components/LoanDetails/LoanDetails.tsx";
import InternalRulesA from "@/pages/InternalRulesA.tsx";
import SelfiePhotoLocVerification from "@/pages/SelfiePhotoLocVerification.tsx";
import ShopOnlyPhotoLocVerification from "@/pages/ShopOnlyPhotoLocVerification.tsx";
import MasDigioJourney from "@/pages/MasDigioJourney.tsx";
import InternalRulesB from "@/pages/InternalRulesB.tsx";
import BankAccountAuthentication from "@/pages/BankAccountAuthentication.tsx";
import SetupMandates from "@/pages/SetupMandates.tsx";
import UpiMandate from "@/pages/UpiMandate.tsx";
import EnachMandate from "@/pages/EnachMandate.tsx";
import SelfieAadhaarMatch from "@/pages/SelfieAadhaarMatch.tsx";
import FinalKFS from "@/pages/FinalKFS.tsx";
import ESignJourney from "@/pages/ESignJourney.tsx";
import DocPushJourney from "@/pages/DocPushJourney.tsx";
import NameMatchVerification, { AADHAAR_IDENTITY_ENDPOINTS, BANK_FINAL_ENDPOINTS } from "@/pages/NameMatchVerification.tsx";
import WaitingForUpdates from "@/pages/WaitingForUpdates.tsx";
import LoanSuccess from "@/pages/LoanSuccess.tsx";
import FinalRejectState from "@/pages/FinalRejectState.tsx";
import NiyoginBREComponent from "@/components/LOS/Niyogin/NiyoginBRE.tsx";
import NiyoginCreateCustomerComponent from "@/components/LOS/Niyogin/NiyoginCreateCustomer.tsx";
import NiyoginInitiateDigilockerComponent from "@/components/LOS/Niyogin/NiyoginInitiateDigilocker.tsx";
import NiyoginUpdateCustomerComponent from "@/components/LOS/Niyogin/NiyoginUpdateCustomer.tsx";
import NiyoginCreateLoanComponent from "@/components/LOS/Niyogin/NiyoginCreateLoan.tsx";
import NiyoginGetLoanStatusComponent from "@/components/LOS/Niyogin/NiyoginGetLoanStatus.tsx";
import NiyoginAAConsentComponent from "@/components/LOS/Niyogin/NiyoginAAConsent.tsx";
import NiyoginAAStatusComponent from "@/components/LOS/Niyogin/NiyoginAAStatus.tsx";
import NiyoginAAFlowComponent from "@/components/LOS/Niyogin/NiyoginAAFlow.tsx";
import NiyoginKFSComponent from "@/components/LOS/Niyogin/NiyoginKFS.tsx";
// Non-Captive Term Loan Components (Generic for multi-lender support)
import Landing from "@/components/LOS/Caprion/Landing.tsx";
import Consent from "@/components/LOS/Caprion/Consent.tsx";
import LenderSelection from "@/components/LOS/Caprion/LenderSelection.tsx";
// Caprion-Specific Components
import CaprionStatusCheck from "@/components/LOS/Caprion/CaprionStatusCheck.tsx";
import CaprionFinalOffer from "@/components/LOS/Caprion/CaprionFinalOffer.tsx";
import CaprionAgreementStatus from "@/components/LOS/Caprion/CaprionAgreementStatus.tsx";
import CaprionDealerApproval from "@/components/LOS/Caprion/CaprionDealerApproval.tsx";
import CaprionAadhaarVerification from "@/components/LOS/Caprion/CaprionAadhaarVerification.tsx";
import CaprionDocuments from "@/components/LOS/Caprion/CaprionDocuments.tsx";
import CaprionAgreementPdf from "@/components/LOS/Caprion/CaprionAgreementPdf.tsx";
import CaprionESign from "@/components/LOS/Caprion/CaprionESign.tsx";
import CaprionAAConsent from "@/components/LOS/Caprion/CaprionAAConsent.tsx";
import CaprionAAStatus from "@/components/LOS/Caprion/CaprionAAStatus.tsx";
import CaprionAAConcentFlow from "@/components/LOS/Caprion/CaprionAAConcentFlow.tsx";
import CaprionLoanVerification from "@/components/LOS/Caprion/CaprionLoanVerification.tsx";
import BureauVerification from "@/components/LOS/Caprion/BureauVerification.tsx";
import SelfiePhotoCapture from "@/apps/los/components/SelfiePhotoCapture";
import ShopPhotoCapture from "@/apps/los/components/ShopPhotoCapture";
// Atomic components for Caprion login flow
import MobileOTPLogin from "@/apps/los/components/MobileOTPLogin/MobileOTPLogin.tsx";
import TermsConsentCheckbox from "@/apps/los/components/TermsConsentCheckbox/TermsConsentCheckbox.tsx";
import PerfiosConsentCheckbox from "@/apps/los/components/PerfiosConsentCheckbox/PerfiosConsentCheckbox.tsx";
import DigioMandateSetup from "@/apps/los/components/DigioMandateSetup";
import BankDetailsWrapper from "@/apps/los/components/BankDetailsWrapper/BankDetailsWrapper.tsx";
import TextBlock from "@/apps/los/components/TextBlock/TextBlock.tsx";
import ImageBlock from "@/apps/los/components/ImageBlock/ImageBlock.tsx";
import ProgressBar from "@/apps/los/components/ProgressBar/ProgressBar.tsx";
import SegmentedProgressBar from "@/apps/los/components/ProgressBar/SegmentedProgressBar.tsx";
import UdhyamFetch from "@/pages/UdhyamFetch.tsx";
import DynamicFormBuilder from "@/apps/los/components/DynamicFormBuilder/DynamicFormBuilder.tsx";


const componentRegistry: Record<string, RegistryEntry> = {
  // --- Atomic Components: Personal Info ---
  "LOS_IndividualNameInput_011": {
    component: IndividualNameInput,
    metadata: {
      id: "LOS_IndividualNameInput_011",
      name: "Full Name",
      icon: "User",
      category: "Personal Information",
      description: "Captures the user's salutation, first name, and last name.",
      validation: {
        schema: z.object({
          salutation: z.string().min(1, "Salutation is required"),
          first_name: z.string().min(1, "First name is required"),
          // Optional: a single-word legal name (mononym) is common on Indian KYC
          // documents and must not block the journey.
          last_name: z.string().optional(),
        }),
        defaultValues: { salutation: "", first_name: "", last_name: "" },
      },
      api: { getter: "LOS_GetIndividualName_011", setter: "LOS_SetIndividualName_012" },
    },
  },
  "LOS_EmailInput_012": {
    component: EmailInput,
    metadata: {
      id: "LOS_EmailInput_012",
      name: "Email Address",
      icon: "Mail",
      category: "Personal Information",
      description: "Captures the user's email address with validation.",
      validation: {
        schema: z.object({
          email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
        }),
        defaultValues: { email: "" },
      },
      api: { getter: "LOS_GetEmail_012", setter: "LOS_SetEmail_013" },
    },
  },
  "LOS_DateOfBirth_013": {
    component: (props: any) => {
        const { date_of_birth } = props.value || {};
        const dayRef = React.useRef<HTMLInputElement>(null);
        const monthRef = React.useRef<HTMLInputElement>(null);
        const yearRef = React.useRef<HTMLInputElement>(null);
        const dateInputRef = React.useRef<HTMLInputElement>(null);
        
        const [day, setDay] = React.useState('');
        const [month, setMonth] = React.useState('');
        const [year, setYear] = React.useState('');

        // Parse date_of_birth into day, month, year.
        // Also normalise the format: getter returns YYYY-MM-DD but the setter expects DD-MM-YYYY.
        // Calling props.onChange here ensures localState always holds DD-MM-YYYY so the
        // orchestrator setter receives the correct format even if the user never edits the field.
        React.useEffect(() => {
            console.log(`[DOB] useEffect fired with date_of_birth=${date_of_birth}`);
            if (date_of_birth) {
                const parts = date_of_birth.split('-');
                if (parts.length === 3) {
                    let d: string, m: string, y: string;
                    if (parts[0].length <= 2) {
                        // Already DD-MM-YYYY
                        [d, m, y] = parts;
                    } else {
                        // YYYY-MM-DD → reorder
                        [y, m, d] = parts;
                    }
                    setDay(d);
                    setMonth(m);
                    setYear(y);
                    // Normalise localState to DD-MM-YYYY so the setter always gets the right format
                    const normalised = `${d}-${m}-${y}`;
                    console.log(`[DOB] useEffect normalised=${normalised} original=${date_of_birth} willCallOnChange=${normalised !== date_of_birth}`);
                    if (normalised !== date_of_birth) {
                        props.onChange({ date_of_birth: normalised });
                    }
                }
            } else {
                setDay('');
                setMonth('');
                setYear('');
            }
        }, [date_of_birth]);

        const updateParent = (d: string, m: string, y: string) => {
            console.log(`[DOB] updateParent called: d=${d} m=${m} y=${y} complete=${d.length === 2 && m.length === 2 && y.length === 4}`);
            if (d.length === 2 && m.length === 2 && y.length === 4) {
                const val = `${d}-${m}-${y}`;
                console.log(`[DOB] calling props.onChange with date_of_birth=${val}`);
                props.onChange({ date_of_birth: val });
            }
        };

        const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 2);
            setDay(value);
            if (value.length === 2) {
                setTimeout(() => {
                    monthRef.current?.focus();
                    monthRef.current?.select();
                }, 0);
            }
            updateParent(value, month, year);
        };

        const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 2);
            setMonth(value);
            if (value.length === 2) {
                setTimeout(() => {
                    yearRef.current?.focus();
                    yearRef.current?.select();
                }, 0);
            }
            updateParent(day, value, year);
        };

        const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 4);
            setYear(value);
            updateParent(day, month, value);
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'day' | 'month' | 'year') => {
            if (e.key === 'Backspace') {
                if (field === 'month' && month === '' && day) {
                    dayRef.current?.focus();
                } else if (field === 'year' && year === '' && month) {
                    monthRef.current?.focus();
                }
            }
        };

        const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const isoValue = e.target.value; // YYYY-MM-DD
            if (isoValue) {
                const [y, m, d] = isoValue.split('-');
                setDay(d);
                setMonth(m);
                setYear(y);
                props.onChange({ date_of_birth: `${d}-${m}-${y}` });
            }
        };

        const handleCalendarClick = () => {
            dateInputRef.current?.showPicker?.();
        };

        const toISOFormat = () => {
            if (day.length === 2 && month.length === 2 && year.length === 4) {
                return `${year}-${month}-${day}`;
            }
            return '';
        };

        const dobError = props.fieldErrors?.['date_of_birth'] || '';
        return (
            <div>
                <label className="text-sm font-medium text-gray-700">Date of Birth <span className="text-red-500">*</span></label>
                <div className={`relative flex items-center border rounded-md px-3 py-2 mt-1 bg-white${dobError ? ' border-red-500' : ''}`} style={{ gap: 0 }}>
                    <input
                        ref={dayRef}
                        type="text"
                        inputMode="numeric"
                        value={day}
                        onChange={handleDayChange}
                        onKeyDown={(e) => handleKeyDown(e, 'day')}
                        placeholder="DD"
                        maxLength={2}
                        style={{ width: '1.25rem', padding: 0, margin: 0, textAlign: 'center' }}
                        className="border-0 focus:outline-none focus:ring-0"
                    />
                    <span className="text-gray-600" style={{ padding: 0, margin: 0, lineHeight: 1 }}>-</span>
                    <input
                        ref={monthRef}
                        type="text"
                        inputMode="numeric"
                        value={month}
                        onChange={handleMonthChange}
                        onKeyDown={(e) => handleKeyDown(e, 'month')}
                        placeholder="MM"
                        maxLength={2}
                        style={{ width: '1.25rem', padding: 0, margin: 0, textAlign: 'center' }}
                        className="border-0 focus:outline-none focus:ring-0"
                    />
                    <span className="text-gray-600" style={{ padding: 0, margin: 0, lineHeight: 1 }}>-</span>
                    <input
                        ref={yearRef}
                        type="text"
                        inputMode="numeric"
                        value={year}
                        onChange={handleYearChange}
                        onKeyDown={(e) => handleKeyDown(e, 'year')}
                        placeholder="YYYY"
                        maxLength={4}
                        style={{ width: '2.5rem', padding: 0, margin: 0, textAlign: 'center' }}
                        className="border-0 focus:outline-none focus:ring-0"
                    />
                    <input
                        ref={dateInputRef}
                        type="date"
                        value={toISOFormat()}
                        onChange={handleDatePickerChange}
                        max={new Date().toISOString().split('T')[0]}
                        min="1900-01-01"
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                        style={{ zIndex: 10 }}
                    />
                    <svg
                        className="ml-auto text-gray-400 pointer-events-none"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                </div>
                {dobError && (
                    <p className="text-red-500 text-xs mt-1">{dobError}</p>
                )}
            </div>
        );
    },
    metadata: {
      id: "LOS_DateOfBirth_013",
      name: "Date of Birth",
      icon: "Calendar",
      category: "Personal Information",
      description: "Captures the user's date of birth in DD/MM/YYYY format.",
      validation: {
        schema: z.object({ date_of_birth: z.string().min(1, "Date of birth is required") }),
        defaultValues: { date_of_birth: "" },
      },
      api: { getter: "LOS_GetDateOfBirth_013", setter: "LOS_SetDateOfBirth_014" },
    }
  },
  "LOS_GenderSelection_015": {
    component: (props: any) => {
        const { gender } = props.value || {};
        const options = [
            { label: 'Male', val: 'm' },
            { label: 'Female', val: 'f' },
            { label: 'Other', val: 'o' }
        ];
        const genderError = props.fieldErrors?.['gender'] || '';
        return (
            <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block text-gray-500">Gender</label>
                <div className="flex gap-4">
                    {options.map(opt => (
                        <button
                            key={opt.val}
                            onClick={() => props.onChange({ gender: opt.val })}
                            className={`px-4 py-2 rounded-xl border-2 transition-all ${
                                (gender === opt.val || gender === opt.label)
                                ? 'bg-intellend-green text-white border-intellend-green shadow-md'
                                : 'bg-white text-gray-700 border-gray-100 hover:border-gray-200'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                {genderError && (
                    <p className="text-red-500 text-xs mt-2">{genderError}</p>
                )}
            </div>
        );
    },
    metadata: {
      id: "LOS_GenderSelection_015",
      name: "Gender Selection",
      icon: "Users",
      category: "Personal Information",
      description: "Captures the user's gender.",
      validation: {
        schema: z.object({ gender: z.string().min(1, "Gender is required") }),
        defaultValues: { gender: "" },
      },
      api: { getter: "LOS_GetGender_015", setter: "LOS_SetGender_016" },
    }
  },
  "LOS_IndividualAadhaarInput_026": {
    component: IndividualAadhaarInput,
    metadata: {
      id: "LOS_IndividualAadhaarInput_026",
      name: "Aadhaar Number",
      icon: "Fingerprint",
      category: "Personal Information",
      description: "Captures the user's Aadhaar number for verification.",
      validation: {
        schema: z.object({ aadhaar: z.string().length(12, "Aadhaar must be 12 digits") }),
        defaultValues: { aadhaar: "" },
      },
      api: { getter: "LOS_GetAadhaar_026", setter: "LOS_SetAadhaar_027" },
    }
  },
  "LOS_IndividualPanInput_028": {
    component: IndividualPANInput,
    metadata: {
      id: "LOS_IndividualPanInput_028",
      name: "PAN Number",
      icon: "CreditCard",
      category: "Personal Information",
      description: "Captures the user's PAN number.",
      validation: {
        schema: z.object({ pan: z.string().length(10, "PAN must be 10 characters") }),
        defaultValues: { pan: "" },
      },
      api: { getter: "LOS_GetIndividualPan_028", setter: "LOS_SetIndividualPan_029" },
    }
  },
  "LOS_PhoneNumberInput_035": {
    component: PhoneNumberInput,
    metadata: {
      id: "LOS_PhoneNumberInput_035",
      name: "Phone Number",
      icon: "Phone",
      category: "Personal Information",
      description: "Captures the user's mobile phone number.",
      validation: {
        schema: z.object({ phone: z.string().length(10, "Phone number must be 10 digits") }),
        defaultValues: { phone: "" },
      },
      api: { getter: "LOS_GetPhoneNumber_035", setter: "LOS_SetPhoneNumber_036" },
    }
  },
  
  // --- Atomic Components: Business Info ---
  "LOS_BusinessPanInput_019": {
    component: CompanyPANInput,
    metadata: {
      id: "LOS_BusinessPanInput_019",
      name: "Business PAN",
      icon: "Briefcase",
      category: "Business Information",
      description: "Captures the business's PAN number.",
      fields: ["company_pan_number"],
      api: { getter: "LOS_GetCompanyPan_019", setter: "LOS_SetCompanyPan_020" },
    }
  },
  "LOS_BusinessVintage_020": {
    component: BusinessVintageInput,
    metadata: {
      id: "LOS_BusinessVintage_020",
      name: "Business Vintage",
      icon: "CalendarCheck",
      category: "Business Information",
      description: "The age of the business in years.",
      fields: ["business_vintage"],
      api: { getter: "LOS_GetBusinessVintage_020", setter: "LOS_SetBusinessVintage_021" },
    }
  },
  "LOS_ConstitutionType_021": {
    component: BusinessConstitutionInput,
    metadata: {
      id: "LOS_ConstitutionType_021",
      name: "Business Constitution",
      icon: "Building",
      category: "Business Information",
      description: "Type of business constitution (e.g., Sole Proprietorship, Partnership).",
      fields: ["business_constitution_type"],
      api: { getter: "LOS_GetBusinessConstitution_066", setter: "LOS_SetBusinessConstitution_067" },
    }
  },
  "LOS_GstInput_025": {
    component: GSTNumberInput,
    metadata: {
      id: "LOS_GstInput_025",
      name: "GST Number",
      icon: "Receipt",
      category: "Business Information",
      description: "The business's GSTIN number.",
      fields: ["gst_number"],
      api: { getter: "LOS_GetGSTNumber_025", setter: "LOS_SetGSTNumber_026" },
    }
  },
  "LOS_NatureOfBusiness_032": {
    component: NatureOfBusinessInput,
    metadata: {
      id: "LOS_NatureOfBusiness_032",
      name: "Nature of Business",
      icon: "Factory",
      category: "Business Information",
      description: "The industry or sector the business operates in.",
      fields: ["nature_of_business"],
      api: { getter: "LOS_GetNatureOfBusiness_032", setter: "LOS_SetNatureOfBusiness_033" },
    }
  },
  "LOS_UdhyamRegistration_039": {
    component: UdhyamRegistrationInput,
    metadata: {
      id: "LOS_UdhyamRegistration_039",
      name: "Udhyam Registration",
      icon: "Award",
      category: "Business Information",
      description: "Udhyam registration number for MSMEs.",
      fields: ["udyam_number"],
      api: { getter: "LOS_GetUdhyamNumber_039", setter: "LOS_SetUdhyamNumber_040" },
    }
  },
  "LOS_ReasonForLoan_033": {
    component: ReasonForLoanInput,
    metadata: {
      id: "LOS_ReasonForLoan_033",
      name: "Reason for Loan",
      icon: "HelpCircle",
      category: "Business Information",
      description: "The reason for taking the loan (dayToDayExpenses, newLocation, growthCapital).",
      fields: ["reason_for_loan"],
      validation: {
        schema: z.object({ reason_for_loan: z.string().min(1, "Reason for loan is required") }),
        defaultValues: { reason_for_loan: "" },
      },
      api: { getter: "LOS_GetLoanPurpose_031", setter: "LOS_SetLoanPurpose_032" },
    }
  },
  "LOS_OtherBusinesses_034": {
    component: OtherBusinessesInput,
    metadata: {
      id: "LOS_OtherBusinesses_034",
      name: "Other Businesses",
      icon: "Building2",
      category: "Business Information",
      description: "Number of other businesses owned (one, twoToThree, threeOrMore).",
      fields: ["other_businesses"],
      validation: {
        schema: z.object({ other_businesses: z.string().min(1, "Number of businesses is required") }),
        defaultValues: { other_businesses: "" },
      },
      api: { getter: "LOS_GetOtherBusinesses_035", setter: "LOS_SetOtherBusinesses_036" },
    }
  },

  // --- Atomic Components: Address ---
  "LOS_AddressFields_017": {
    component: CompositeAddress,
    metadata: {
      id: "LOS_AddressFields_017",
      name: "Address Fields",
      icon: "MapPin",
      category: "Address",
      description: "A composite component for address input."
    }
  },
  "LOS_PincodeInput_036": {
    component: () => null as any, // PincodeInput
    metadata: {
      id: "LOS_PincodeInput_036",
      name: "Pincode",
      icon: "Map",
      category: "Address",
      description: "Captures a 6-digit postal pincode."
    }
  },

  // --- Atomic Components: Loan Details ---
  "LOS_LoanAmount_029": {
    component: LoanAmountInput,
    metadata: {
      id: "LOS_LoanAmount_029",
      name: "Loan Amount",
      icon: "CircleDollarSign",
      category: "Loan Details",
      description: "The requested loan amount.",
      fields: ["loan_amount"],
      api: { getter: "LOS_GetLoanAmount_029", setter: "LOS_SetLoanAmount_030" },
    }
  },
  "LOS_LoanDetails_030": {
    component: () => null as any, // LoanDetails (deprecated - use LOS_LenderOfferSelection_031 instead)
    metadata: {
      id: "LOS_LoanDetails_030",
      name: "Loan Details",
      icon: "Info",
      category: "Loan Details",
      description: "Displays comprehensive details of a loan."
    }
  },
  "LOS_LenderOfferSelection_031": {
    component: LenderOfferSelection,
    metadata: {
      id: "LOS_LenderOfferSelection_031",
      name: "Lender Offer Selection",
      icon: "DollarSign",
      category: "Loan Details",
      description: "Interactive lender offer selection with 3x3 matrix (tenure × repayment type). Replaces LoanDetails with user-selectable loan parameters. Component fetches and saves data directly, no orchestrator calls.",
      fields: ["loan_amount", "loan_tenure", "repayment_method", "interest_rate"],
      // No API - component handles both fetching and saving directly
    }
  },
  "LOS_LoanPurpose_031": {
    component: LoanPurposeInput,
    metadata: {
      id: "LOS_LoanPurpose_031",
      name: "Purpose of Loan",
      icon: "HelpCircle",
      category: "Loan Details",
      description: "The reason for taking the loan.",
      fields: ["loan_purpose"],
      api: { getter: "LOS_GetLoanPurpose_031", setter: "LOS_SetLoanPurpose_032" },
    }
  },
  "LOS_Tenure_038": {
    component: LoanTenureInput,
    metadata: {
      id: "LOS_Tenure_038",
      name: "Loan Tenure",
      icon: "Clock",
      category: "Loan Details",
      description: "The repayment period for the loan in months.",
      fields: ["loan_tenure"],
      api: { getter: "LOS_GetLoanTenure_038", setter: "LOS_SetLoanTenure_039" },
    }
  },
  "LOS_LoanAmountSlider_041": {
    component: LoanAmountSliderInput,
    metadata: {
      id: "LOS_LoanAmountSlider_041",
      name: "Loan Amount (Slider)",
      icon: "Slider",
      category: "Loan Details",
      description: "Slider input for loan amount selection (₹50K - ₹5L)",
      fields: ["loan_amount"],
      api: {
        getter: "LOS_GetLoanAmount_029",
        setter: "LOS_SetLoanAmount_030"
      },
    }
  },
  "LOS_OfferCongratulationsMessage_450": {
    component: OfferCongratulationsMessage,
    metadata: {
      id: "LOS_OfferCongratulationsMessage_450",
      name: "Offer Congratulations Message",
      icon: "PartyPopper",
      category: "Display",
      description: "Congratulations banner for loan offer pages",
      fields: [],
    }
  },
  "LOS_LoanTenureDropdown_042": {
    component: LoanTenureDropdownInput,
    metadata: {
      id: "LOS_LoanTenureDropdown_042",
      name: "Loan Tenure (Dropdown)",
      icon: "Calendar",
      category: "Loan Details",
      description: "Dropdown for loan tenure selection (6-36 months)",
      fields: ["loan_tenure"],
      api: {
        getter: "LOS_GetLoanTenure_038",
        setter: "LOS_SetLoanTenure_039"
      },
    }
  },

  // --- Atomic Components: Verification & Documents ---
  "LOS_CreditScoreCheck_016": {
    component: CreditScoreCheck,
    metadata: {
      id: "LOS_CreditScoreCheck_016",
      name: "Credit Score Check",
      icon: "CheckCircle",
      category: "Verification",
      description: "Performs a credit score check."
    }
  },
  "LOS_BankStatementCollection_025": {
    component: BankStatementCollection,
    metadata: {
      id: "LOS_BankStatementCollection_025",
      name: "Bank Statement Verification",
      icon: "Landmark",
      category: "Verification",
      description: "Collects and analyzes bank statements via PDF upload or NetBanking.",
      api: { getter: "LOS_GetCartConfig_020" },
    }
  },
  "LOS_BusinessDocumentUpload_018": {
    component: () => null as any, // BusinessDocumentUpload
    metadata: {
      id: "LOS_BusinessDocumentUpload_018",
      name: "Business Document Upload",
      icon: "UploadCloud",
      category: "Documents",
      description: "Component for uploading business-related documents."
    }
  },
  "LOS_DirectorAadhaar_022": {
    component: () => null as any, // DirectorAadhaar
    metadata: {
      id: "LOS_DirectorAadhaar_022",
      name: "Director's Aadhaar",
      icon: "Fingerprint",
      category: "Director Details",
      description: "Captures the Aadhaar of a company director."
    }
  },
  "LOS_DirectorPan_023": {
    component: () => null as any, // DirectorPan
    metadata: {
      id: "LOS_DirectorPan_023",
      name: "Director's PAN",
      icon: "CreditCard",
      category: "Director Details",
      description: "Captures the PAN of a company director."
    }
  },
  "LOS_IndividualDocumentUpload_027": {
    component: () => null as any, // IndividualDocumentUpload
    metadata: {
      id: "LOS_IndividualDocumentUpload_027",
      name: "Individual Document Upload",
      icon: "Upload",
      category: "Documents",
      description: "Component for uploading personal documents."
    }
  },
   "LOS_PartnerAadhaar_033": {
    component: () => null as any, // PartnerAadhaar
    metadata: {
      id: "LOS_PartnerAadhaar_033",
      name: "Partner's Aadhaar",
      icon: "Fingerprint",
      category: "Partner Details",
      description: "Captures the Aadhaar of a business partner."
    }
  },
  "LOS_PartnerPan_034": {
    component: () => null as any, // PartnerPan
    metadata: {
      id: "LOS_PartnerPan_034",
      name: "Partner's PAN",
      icon: "CreditCard",
      category: "Partner Details",
      description: "Captures the PAN of a business partner."
    }
  },

  // --- Atomic Components: Other ---
  "LOS_FinancialDetails_024": {
    component: () => null as any, // FinancialDetails
    metadata: {
      id: "LOS_FinancialDetails_024",
      name: "Financial Details",
      icon: "Landmark",
      category: "Financials",
      description: "Captures detailed financial information."
    }
  },
  "LOS_ReviewAndSubmit_037": {
    component: () => null as any, // ReviewAndSubmit
    metadata: {
      id: "LOS_ReviewAndSubmit_037",
      name: "Review and Submit",
      icon: "Send",
      category: "Actions",
      description: "Final step to review all details and submit the application."
    }
  },

  // --- Layout & UI Components ---
  "LOS_Row_050": { 
    component: Row,
    metadata: {
      id: "LOS_Row_050",
      name: "Row",
      icon: "RectangleHorizontal",
      category: "Layout",
      description: "A layout component to arrange items in a row."
    }
  },
  "LOS_Grid_051": {
    component: Grid,
    metadata: {
      id: "LOS_Grid_051",
      name: "Grid",
      icon: "LayoutGrid",
      category: "Layout",
      description: "A layout component to arrange items in a grid."
    }
  },
  "LOS_Header_047": {
    component: Header,
    metadata: {
      id: "LOS_Header_047",
      name: "Header",
      icon: "PanelTop",
      category: "UI",
      description: "The main application header."
    }
  },

  // --- Journey & System Components ---
  "LOS_JourneyAnalysis_040": {
    component: JourneyAnalysis,
    metadata: {
      id: "LOS_JourneyAnalysis_040",
      name: "Journey Analysis",
      icon: "LineChart",
      category: "System",
      description: "A component for analyzing journey metrics."
    }
  },
  "LOS_MobileGuard_042": {
    component: MobileGuard,
    metadata: {
      id: "LOS_MobileGuard_042",
      name: "Mobile Guard",
      icon: "Smartphone",
      category: "System",
      description: "Restricts access to mobile devices only."
    }
  },
  "LOS_JourneyHeader_043": {
    component: JourneyHeader,
    metadata: {
      id: "LOS_JourneyHeader_043",
      name: "Journey Header",
      icon: "PanelTop",
      category: "UI",
      description: "A specific header for user journeys."
    }
  },
  "LOS_BrandedJourneyHeader_900": {
    component: BrandedJourneyHeader,
    metadata: {
      id: "LOS_BrandedJourneyHeader_900",
      name: "Branded Journey Header",
      icon: "PanelTop",
      category: "UI",
      description: "Branded header with Intellend logo and customizable title/subtitle for journey pages."
    }
  },
  "LOS_JourneySync_046": {
    component: JourneySync,
    metadata: {
      id: "LOS_JourneySync_046",
      name: "Journey Sync",
      icon: "RefreshCw",
      category: "System",
      description: "Handles state synchronization during a journey.",
      invisible: true,
    }
  },
  "LOS_ProtectedRoute_058": {
    component: ProtectedRoute,
    metadata: {
      id: "LOS_ProtectedRoute_058",
      name: "Protected Route",
      icon: "Shield",
      category: "System",
      description: "A route that requires authentication."
    }
  },
  
  // NOTE: More stubs for components found in /apps/los/components that still need full metadata.
  // This provides a more complete, though not fully detailed, registry.
  "LOS_BusinessActivityInput_059": { component: BusinessActivityInput, metadata: { id: "LOS_BusinessActivityInput_059", name: "Business Activity", icon: "Activity", category: "Business Information", description: "Describes the primary business activity.", fields: ["business_activity"], api: { getter: "LOS_GetBusinessActivity_059", setter: "LOS_SetBusinessActivity_060" } }},
  
  "LOS_LoanOfferCard_XXX": { component: LoanOfferCard, metadata: { id: "LOS_LoanOfferCard_XXX", name: "Loan Offer Card", icon: "CreditCard", category: "Loan Details", description: "Displays a single loan offer." }},
  "LOS_SelfieCapture_XXX": { component: () => null as any, metadata: { id: "LOS_SelfieCapture_XXX", name: "Selfie Capture", icon: "Camera", category: "Verification", description: "Capture user selfie." }},

  // --- Internal Verification Triggers ---
  "LOS_InternalRulesA_436": {
    component: InternalRulesA,
    metadata: {
      id: "LOS_InternalRulesA_436",
      name: "Verification Set A (Internal)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Auto-fires POST /api/trigger_ruleset_a/ on mount. Runs all mandatory verifications after the first 3 data pages (Personal, Business, Additional Info). On failure redirects to /final-reject-state; on pass calls onDone() to advance the journey. Shows a spinner UI to the user.",
      autoFire: true,
      selfAdvances: true,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_InternalRulesB_437": {
    component: InternalRulesB,
    metadata: {
      id: "LOS_InternalRulesB_437",
      name: "Verification Set B (Internal)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Auto-fires POST /api/trigger_ruleset_b/ on mount. Runs all mandatory verifications after the bank account auth + mandate pages. On failure redirects to /final-reject-state; on pass calls onDone() to advance the journey. Retries automatically after 30s on network error.",
      autoFire: true,
      selfAdvances: true,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },

  // ── Name Match + DigiLocker Verification (Caprion) ────────────────────────
  // All 12 IDs live on the same page. Only the FIRST one (V01) mounts the full
  // NameMatchVerification component that fires all 11 API calls + trigger_ruleset_b
  // and calls onDone(). The remaining 11 are no-ops (null renders) so we don't
  // fire duplicate calls. Add all 12 IDs to the Name Match Verification page
  // in the builder to get the full verification suite.
  "LOS_VerifyDigitapPrefillName_V01": {
    // Wrapped (not passed bare) so no_advance reaches /api/trigger_ruleset_b/.
    // Same reason as LOS_BankFinalVerification_V31 below: this component is
    // selfAdvances:false, so JourneyPage performs the advance — the endpoint
    // must not also advance on pass / write REJECTED on fail.
    component: (props: any) => (
      <NameMatchVerification {...props} requestBody={{ no_advance: true }} />
    ),
    metadata: {
      id: "LOS_VerifyDigitapPrefillName_V01",
      name: "Name Match Verification (All 11 checks)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Auto-fires all 11 Caprion name/identity verification checks sequentially on mount (DigiTap prefill name, Aadhaar name, PAN name, bank account name, PAN–Aadhaar linkage, Aadhaar gender, Aadhaar DOB, DigiTap prefill DOB, DigiTap prefill PAN, PAN DOB, PAN gender) then trigger_ruleset_b. Calls onDone() when complete. Retries on 5xx/network errors.",
      autoFire: true,
      selfAdvances: false,  // backend does NOT advance stage — JourneyPage must call logJourneyEvent(advance:true)
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  // V02–V11 are no-ops: they share the same page as V01 and must not fire
  // duplicate API calls. Rendering null keeps DynamicPage happy while V01
  // handles all the work.
  "LOS_VerifyAadhaarName_V02": {
    component: () => null,
    metadata: {
      id: "LOS_VerifyAadhaarName_V02",
      name: "Aadhaar Name Match (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires all 7 checks. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_VerifyPanName_V03": {
    component: () => null,
    metadata: {
      id: "LOS_VerifyPanName_V03",
      name: "PAN Name Match (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires all 7 checks. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_VerifyBankAccountName_V04": {
    component: () => null,
    metadata: {
      id: "LOS_VerifyBankAccountName_V04",
      name: "Bank Account Name Match (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires all 7 checks. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_VerifyPanAadhaarLinked_V05": {
    component: () => null,
    metadata: {
      id: "LOS_VerifyPanAadhaarLinked_V05",
      name: "PAN–Aadhaar Linkage (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires all 7 checks. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_VerifyAadhaarGender_V06": {
    component: () => null,
    metadata: {
      id: "LOS_VerifyAadhaarGender_V06",
      name: "Aadhaar Gender Check (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires all 7 checks. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_VerifyAadhaarDob_V07": {
    component: () => null,
    metadata: {
      id: "LOS_VerifyAadhaarDob_V07",
      name: "Aadhaar DOB Check (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires all 7 checks. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_VerifyPrefillDob_V08": {
    component: () => null,
    metadata: {
      id: "LOS_VerifyPrefillDob_V08",
      name: "DigiTap Prefill DOB Check (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires all checks. Compares DigiTap Mobile Prefill DOB against IndividualDetail.date_of_birth. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_VerifyPrefillPan_V09": {
    component: () => null,
    metadata: {
      id: "LOS_VerifyPrefillPan_V09",
      name: "DigiTap Prefill PAN Check (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires all checks. Compares DigiTap Mobile Prefill PAN against IndividualDetail.individual_pan_number. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  // V10–V11: DigiLocker cross-check verifications (PAN Details Plus vs user input)
  "LOS_VerifyPanDob_V10": {
    component: () => null,
    metadata: {
      id: "LOS_VerifyPanDob_V10",
      name: "PAN DOB Match — DigiLocker (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires all checks. Compares PAN Details Plus result.dob (DD/MM/YYYY) against IndividualDetail.date_of_birth. Part of DigiLocker verification suite. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_VerifyPanGender_V11": {
    component: () => null,
    metadata: {
      id: "LOS_VerifyPanGender_V11",
      name: "PAN Gender Match — DigiLocker (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires all checks. Compares PAN Details Plus result.gender (male/female) against IndividualDetail.gender (m/f/o). Part of DigiLocker verification suite. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  // V12: Final cross-check trigger (trigger_ruleset_b — Aadhaar last-4, Aadhaar-PAN DOB, PAN DOB, PAN gender)
  "LOS_TriggerRulesetB_V12": {
    component: () => null,
    metadata: {
      id: "LOS_TriggerRulesetB_V12",
      name: "Trigger Ruleset B — DigiLocker Cross-checks (no-op slot)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "No-op slot — V01 fires trigger_ruleset_b as the 12th step. Runs AADHAAR_LAST4_MATCH, AADHAAR_PAN_DOB_MATCH, PAN_INDIVIDUAL_DOB_MATCH, PAN_INDIVIDUAL_GENDER_MATCH and advances the journey. This ID must be present on the page config but renders nothing.",
      autoFire: false,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },

  // ── Split Verification Pages (Caprion) ────────────────────────────────────
  // The original NameMatchVerification (V01) fires all 12 checks on one page.
  // These two new IDs split that into two separate auto-fire pages:
  //
  //   V30 — "Aadhaar & Identity Verification" — placed RIGHT AFTER Aadhaar Verification
  //          Fires: DigiTap prefill name, Aadhaar name, PAN name, PAN–Aadhaar linkage,
  //                 Aadhaar gender, Aadhaar DOB, DigiTap prefill DOB, DigiTap prefill PAN,
  //                 PAN DOB, PAN gender  (all DigiLocker/Aadhaar/PAN checks)
  //          Rationale: if these fail, there is no point proceeding to bank/mandate.
  //
  //   V31 — "Bank & Final Verification" — placed AFTER Digio eMandate Setup
  //          Fires: Bank account name match + trigger_ruleset_b (final cross-checks)
  //          Rationale: bank name can only be checked after bank details are entered.
  //
  // Both pages have count_in_progress: false (observer pages, not counted in progress bar).
  // V30 also persists the address-score/CPV snapshot via `postRunEndpoint`.
  // This page is the right place for it: it runs immediately after Aadhaar
  // Verification, which is the first point where EVERY source the category
  // formula needs is on file (b4 Aadhaar, c4 PAN Plus, d4 Udyam, e4 Digitap,
  // f4/g4 selfie GPS). Scoring earlier would store b4/f4/g4 empty and an
  // understated category for no reason other than page ordering.
  // NON-BLOCKING: a scoring failure is swallowed and never gates the user —
  // the thresholds it uses are uncalibrated estimates, which is exactly what
  // this data is being collected to fix.
  "LOS_AadhaarIdentityVerification_V30": {
    component: (props: any) => (
      <NameMatchVerification
        {...props}
        endpoints={AADHAAR_IDENTITY_ENDPOINTS}
        postRunEndpoint="/api/caprion/address-score-cpv/save/"
        verificationLabel="Aadhaar & Identity Verification"
      />
    ),
    metadata: {
      id: "LOS_AadhaarIdentityVerification_V30",
      name: "Aadhaar & Identity Verification (auto-fire)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Auto-fires 10 DigiLocker/Aadhaar/PAN identity checks on mount: DigiTap prefill name, Aadhaar name, PAN name, PAN–Aadhaar linkage, Aadhaar gender, Aadhaar DOB, DigiTap prefill DOB, DigiTap prefill PAN, PAN DOB, PAN gender. Placed immediately after Aadhaar Verification page. count_in_progress: false.",
      autoFire: true,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_BankFinalVerification_V31": {
    component: (props: any) => (
      <NameMatchVerification
        {...props}
        endpoints={BANK_FINAL_ENDPOINTS}
        verificationLabel="Bank & Final Verification"
        // no_advance: this page fires /api/trigger_ruleset_b/, which by default
        // mutates the journey stage itself — advancing on pass and writing
        // REJECTED on fail. Both fight this component: on FAILURE the stage was
        // moved and then overwritten by the page's own advance, pushing the user
        // straight past the "Bank Verification Failed" card; on SUCCESS the stage
        // got advanced twice (V31 is selfAdvances:false, so JourneyPage advances).
        // With this flag the endpoint only runs the checks and reports the result;
        // blocking (lock endpoint) and advancing (onDone) stay owned here.
        requestBody={{ no_advance: true }}
        // Use a separate meta key + lock endpoint so this page's failure is
        // independent of the Aadhaar identity check failure on V30.
        // Without these overrides both pages share aadhaar_verification_blocked
        // and the bank-failure card incorrectly says "Aadhaar Verification Failed".
        lockEndpoint="/api/caprion/verify/lock-bank-verification/"
        blockedMetaKey="bank_verification_blocked"
        failedChecksMetaKey="bank_verification_failed_checks"
        blockedTitle="Bank Verification Failed"
        blockedDescription="We could not verify your bank account details. Your application has been paused. Please contact our support team for assistance."
      />
    ),
    metadata: {
      id: "LOS_BankFinalVerification_V31",
      name: "Bank & Final Verification (auto-fire)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Auto-fires bank account name match + trigger_ruleset_b (AADHAAR_LAST4_MATCH, AADHAAR_PAN_DOB_MATCH, PAN_INDIVIDUAL_DOB_MATCH, PAN_INDIVIDUAL_GENDER_MATCH) on mount. Placed after Digio eMandate Setup. count_in_progress: false.",
      autoFire: true,
      selfAdvances: false,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },

  // ── Prefill Gate Components (invisible, component-driven, any page) ───────
  // Add any of these IDs to a builder page to gate Continue on that check.
  // They render nothing in the UI — JourneyPage scans for prefillGate metadata.
  "LOS_PrefillGate_Name_V10": {
    component: () => null,
    metadata: {
      id: "LOS_PrefillGate_Name_V10",
      name: "Prefill Gate — Name (invisible)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Invisible gate: blocks Continue if the user's Name does not match the DigiTap Mobile Prefill name. Renders nothing. Add to any page that should enforce this check.",
      autoFire: false,
      selfAdvances: false,
      invisible: true,
      prefillGate: {
        url: "/api/caprion/verify/prefill-name-on-page/",
        label: "Name",
      },
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_PrefillGate_DOB_V11": {
    component: () => null,
    metadata: {
      id: "LOS_PrefillGate_DOB_V11",
      name: "Prefill Gate — Date of Birth (invisible)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Invisible gate: blocks Continue if the user's Date of Birth does not match the DigiTap Mobile Prefill DOB. Renders nothing. Add to any page that should enforce this check.",
      autoFire: false,
      selfAdvances: false,
      invisible: true,
      prefillGate: {
        url: "/api/caprion/verify/prefill-dob-on-page/",
        label: "Date of Birth",
      },
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_PrefillGate_PAN_V12": {
    component: () => null,
    metadata: {
      id: "LOS_PrefillGate_PAN_V12",
      name: "Prefill Gate — PAN (invisible)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Invisible gate: blocks Continue if the user's PAN does not match the DigiTap Mobile Prefill PAN. Renders nothing. Add to any page that should enforce this check.",
      autoFire: false,
      selfAdvances: false,
      invisible: true,
      prefillGate: {
        url: "/api/caprion/verify/prefill-pan-on-page/",
        label: "PAN",
      },
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_PrefillGate_PanAadhaar_V13": {
    component: () => null,
    metadata: {
      id: "LOS_PrefillGate_PanAadhaar_V13",
      name: "Prefill Gate — PAN–Aadhaar Link (invisible)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Invisible gate: blocks Continue if PAN is not linked to Aadhaar (via DigilockerKYCInformation). If DigiLocker KYC has not been completed yet, returns not_applicable and does NOT block. Renders nothing. Add to any page that should enforce this check.",
      autoFire: false,
      selfAdvances: false,
      invisible: true,
      prefillGate: {
        url: "/api/caprion/verify/pan-aadhaar-on-page/",
        label: "PAN–Aadhaar Link",
      },
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_PrefillGate_Gender_V14": {
    component: () => null,
    metadata: {
      id: "LOS_PrefillGate_Gender_V14",
      name: "Prefill Gate — Gender (invisible)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Invisible gate: blocks Continue if the user's selected gender does not match the DigiTap Mobile Prefill gender. Normalises DigiTap MALE/FEMALE to m/f before comparing. If no prefill data, returns not_applicable and does NOT block. Renders nothing. Add to any page that should enforce this check.",
      autoFire: false,
      selfAdvances: false,
      invisible: true,
      prefillGate: {
        url: "/api/caprion/verify/prefill-gender-on-page/",
        label: "Gender",
      },
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  // ── PAN Details Plus Gate Components (invisible, PAN Details Plus-driven, any page) ──
  // These gates compare form values against the PAN Details Plus API response.
  // IMPORTANT: LOS_PanDetailsPlusGate_Refresh_V20 MUST be the first gate in the
  // page component list — it re-calls PAN Details Plus when the user edits their PAN,
  // ensuring all downstream check gates compare against fresh data.
  "LOS_PanDetailsPlusGate_Refresh_V20": {
    component: () => null,
    metadata: {
      id: "LOS_PanDetailsPlusGate_Refresh_V20",
      name: "PAN Details Plus Gate — Refresh (invisible)",
      icon: "RefreshCw",
      category: "Verification",
      description: "Invisible gate: re-calls PAN Details Plus API with the submitted PAN before the check gates run. Idempotent — skips the API call if the PAN is unchanged. If the user edited their PAN, forces a fresh API call so name/DOB/gender/PAN checks compare against the new PAN's data. MUST be listed before all other PAN Details Plus gates on the page. Renders nothing.",
      autoFire: false,
      selfAdvances: false,
      invisible: true,
      prefillGate: {
        url: "/api/caprion/verify/pan-details-plus-refresh/",
        label: "PAN Details Plus Refresh",
      },
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_PanDetailsPlusGate_Name_V21": {
    component: () => null,
    metadata: {
      id: "LOS_PanDetailsPlusGate_Name_V21",
      name: "PAN Details Plus Gate — Name (invisible)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Invisible gate: blocks Continue if the user's name does not match the name returned by PAN Details Plus API (fuzzy match at lender threshold, default 60%). If PAN Details Plus has not been called yet, returns not_applicable and does NOT block. Renders nothing. Add to any page that should enforce this check.",
      autoFire: false,
      selfAdvances: false,
      invisible: true,
      prefillGate: {
        url: "/api/caprion/verify/pan-details-plus-name/",
        label: "Name (PAN Details Plus)",
      },
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_PanDetailsPlusGate_DOB_V22": {
    component: () => null,
    metadata: {
      id: "LOS_PanDetailsPlusGate_DOB_V22",
      name: "PAN Details Plus Gate — Date of Birth (invisible)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Invisible gate: blocks Continue if the user's date of birth does not match the DOB returned by PAN Details Plus API (exact match). If PAN Details Plus has not been called yet or has no DOB, returns not_applicable and does NOT block. Renders nothing.",
      autoFire: false,
      selfAdvances: false,
      invisible: true,
      prefillGate: {
        url: "/api/caprion/verify/pan-details-plus-dob/",
        label: "Date of Birth (PAN Details Plus)",
      },
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_PanDetailsPlusGate_Gender_V23": {
    component: () => null,
    metadata: {
      id: "LOS_PanDetailsPlusGate_Gender_V23",
      name: "PAN Details Plus Gate — Gender (invisible)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Invisible gate: blocks Continue if the user's selected gender does not match the gender returned by PAN Details Plus API. Normalises PAN Details Plus 'male'/'female' to m/f before comparing. If PAN Details Plus has not been called yet or has no gender, returns not_applicable and does NOT block. Renders nothing.",
      autoFire: false,
      selfAdvances: false,
      invisible: true,
      prefillGate: {
        url: "/api/caprion/verify/pan-details-plus-gender/",
        label: "Gender (PAN Details Plus)",
      },
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  "LOS_PanDetailsPlusGate_PAN_V24": {
    component: () => null,
    metadata: {
      id: "LOS_PanDetailsPlusGate_PAN_V24",
      name: "PAN Details Plus Gate — PAN Number (invisible)",
      icon: "ShieldCheck",
      category: "Verification",
      description: "Invisible gate: blocks Continue if the PAN number submitted by the user does not match the PAN returned by PAN Details Plus API (exact match, case-insensitive). Ensures the PAN entered is valid and found in records. If PAN Details Plus has not been called yet, returns not_applicable and does NOT block. Renders nothing.",
      autoFire: false,
      selfAdvances: false,
      invisible: true,
      prefillGate: {
        url: "/api/caprion/verify/pan-details-plus-pan/",
        label: "PAN Number (PAN Details Plus)",
      },
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },
  // ── End PAN Details Plus Gate Components ──────────────────────────────────

  // ── HHI Checkbox Gate Component (visible, Personal Info page) ────────────
  // Renders the "I declare my household income exceeds ₹3 lakhs per annum"
  // checkbox. The prefill gate blocks Continue if the checkbox is not ticked.
  "LOS_HHICheckboxGate_V25": {
    component: HHIConsentCheckbox,
    metadata: {
      id: "LOS_HHICheckboxGate_V25",
      name: "HHI Declaration Checkbox — ₹3 Lakh",
      icon: "CheckSquare",
      category: "Consent",
      description: "Visible checkbox: 'I declare my household income exceeds ₹3 lakhs per annum'. Blocks Continue (via prefill gate) if not ticked. Does NOT check income range from BusinessDetail — checkbox tick only. Uses SOLE_PROP_HHI_THRESHOLD verification code.",
      autoFire: false,
      selfAdvances: false,
      fields: ["declares_hhi_above_3_lakh"],
      api: {
        // Getter reads IndividualDetail.declares_hhi_above_3_lakh so the checkbox
        // is pre-ticked on page reload if the user already declared HHI consent.
        // The fields allowlist above ensures the setter only sends declares_hhi_above_3_lakh
        // back (not the full IndividualDetail object returned by the getter).
        getter: "LOS_GetIndividualDetails_001",
        setter: "LOS_SetIndividualDetails_002",
      },
      prefillGate: {
        url: "/api/caprion/verify/hhi-checkbox-on-page/",
        label: "HHI Declaration (₹3 Lakh)",
      },
      validation: {
        schema: z.object({
          declares_hhi_above_3_lakh: z.boolean().refine((v) => v === true, {
            message: "Please confirm your household income exceeds ₹3 lakhs per annum to proceed.",
          }),
        }),
        defaultValues: { declares_hhi_above_3_lakh: false },
      },
    },
  },

  // ── End HHI Checkbox Gate Component ──────────────────────────────────────
  // ── End Prefill Gate Components ───────────────────────────────────────────

  // ── End Name Match Verification ───────────────────────────────────────────

  "LOS_CompositeBusinessAddress_060": {
    component: CompositeBusinessAddress,
    metadata: {
      id: "LOS_CompositeBusinessAddress_060",
      name: "Business Address",
      icon: "Building2",
      category: "Address",
      description: "A composite component for business address.",
      fields: [
        "business_address_line1",
        "business_address_line2",
        "business_address_line3",
        "business_address_pincode",
        "business_address_landmark",
        "business_address_state",
        // Exact map-pin coordinates from MapAddressPicker — see the note on
        // LOS_CompositePersonalCommunicationAddress_072 below.
        "business_address_latitude",
        "business_address_longitude"
      ],
      validation: {
        schema: z.object({
          business_address_line1: z.string().min(1, "Required"),
          business_address_line2: z.string().optional(),
          business_address_line3: z.string().min(1, "Required"),
          business_address_pincode: z.string().length(6, "Must be 6 digits"),
          business_address_landmark: z.string().optional(),
          business_address_state: z.string().min(1, "State is required"),
        }),
        defaultValues: {},
      },
      api: { getter: "LOS_GetBusinessAddress_061", setter: "LOS_SetBusinessAddress_061" },
    }
  },

  "LOS_CompositeBusinessRegisteredAddress_088": { 
    component: CompositeBusinessRegisteredAddress, 
    metadata: { 
      id: "LOS_CompositeBusinessRegisteredAddress_088", 
      name: "Registered Address", 
      icon: "MapPin", 
      category: "Address", 
      description: "A composite component for registered address.",
      validation: {
        schema: z.object({
          registered_address_line1: z.string().min(1, "Required"),
          registered_address_line2: z.string().optional(),
          registered_address_line3: z.string().min(1, "Required"),
          registered_address_pincode: z.string().length(6, "Must be 6 digits"),
          registered_address_landmark: z.string().optional(),
        }),
        defaultValues: {},
      },
      api: { getter: "LOS_GetBusinessDetails_003", setter: "LOS_SetBusinessDetails_004" },
    }
  },

  "LOS_CompositePersonalCommunicationAddress_072": {
    component: CompositePersonalCommunicationAddress,
    metadata: {
      id: "LOS_CompositePersonalCommunicationAddress_072",
      name: "Communication Address",
      icon: "Mail",
      category: "Address",
      description: "A composite component for communication address.",
      // fields allowlist: only send address fields to LOS_SetIndividualDetails_002.
      // Without this, the getter (LOS_GetIndividualDetails_001) returns the full
      // IndividualDetail object (including date_of_birth, gender, phone_number, etc.)
      // and the setter would overwrite values already saved by dedicated setters
      // (LOS_SetDateOfBirth_014, LOS_SetGender_016, etc.).
      fields: [
        "communication_address_line1",
        "communication_address_line2",
        "communication_address_line3",
        "communication_address_pincode",
        "communication_address_landmark",
        "communication_address_state",
        // Exact map-pin coordinates from MapAddressPicker. Allowlisted so the
        // setter can store Address.geo_point directly instead of relying on
        // backend re-geocoding of the address text (which lands 50-500 m off
        // and makes the selfie location check unreliable).
        "communication_address_latitude",
        "communication_address_longitude",
      ],
      validation: {
        schema: z.object({
          communication_address_line1: z.string().min(1, "Required"),
          communication_address_line2: z.string().optional(),
          communication_address_line3: z.string().min(1, "Required"),
          communication_address_pincode: z.string().length(6, "Must be 6 digits"),
          communication_address_landmark: z.string().optional(),
          communication_address_state: z.string().min(1, "State is required"),
        }),
        defaultValues: {},
      },
      api: { getter: "LOS_GetPersonalAddress_073", setter: "LOS_SetPersonalAddress_073" },
    }
  },

  "LOS_CompositePersonalResidentialAddress_083": {
    component: CompositePersonalResidentialAddress,
    metadata: {
      id: "LOS_CompositePersonalResidentialAddress_083",
      name: "Residential Address",
      icon: "Home",
      category: "Address",
      description: "A composite component for residential address.",
      // fields allowlist: same reason as LOS_CompositePersonalCommunicationAddress_072 —
      // getter returns full IndividualDetail; restrict setter payload to residential
      // address fields only so date_of_birth/gender/etc. are never overwritten.
      fields: [
        "residential_address_line1",
        "residential_address_line2",
        "residential_address_line3",
        "residential_address_pincode",
        "residential_address_landmark",
      ],
      validation: {
        schema: z.object({
          residential_address_line1: z.string().min(1, "Required"),
          residential_address_line2: z.string().optional(),
          residential_address_line3: z.string().min(1, "Required"),
          residential_address_pincode: z.string().length(6, "Must be 6 digits"),
          residential_address_landmark: z.string().optional(),
        }),
        defaultValues: {},
      },
      api: { getter: "LOS_GetIndividualDetails_001", setter: "LOS_SetIndividualDetails_002" },
    }
  },

  "LOS_BusinessConstitutionInput_066": { component: BusinessConstitutionInput, metadata: { id: "LOS_BusinessConstitutionInput_066", name: "Business Constitution", icon: "Building", category: "Business Information", description: "The legal constitution of the business.", fields: ["business_constitution_type"], api: { getter: "LOS_GetBusinessConstitution_066", setter: "LOS_SetBusinessConstitution_067" } }},
  "LOS_BusinessNameInput_067": { component: BusinessNameInput, metadata: { id: "LOS_BusinessNameInput_067", name: "Business Name", icon: "Briefcase", category: "Business Information", description: "The legal name of the business.", fields: ["business_name"], api: { getter: "LOS_GetBusinessName_067", setter: "LOS_SetBusinessName_068" } }},
  "LOS_CompanyPANInput_068": { component: CompanyPANInput, metadata: { id: "LOS_CompanyPANInput_068", name: "Company PAN", icon: "CreditCard", category: "Business Information", description: "The PAN of the company.", fields: ["company_pan_number"], api: { getter: "LOS_GetCompanyPan_019", setter: "LOS_SetCompanyPan_020" } }},
  "LOS_DateOfIncorporationInput_069": { component: DateOfIncorporationInput, metadata: { id: "LOS_DateOfIncorporationInput_069", name: "Date of Incorporation", icon: "CalendarPlus", category: "Business Information", description: "The date the company was incorporated.", fields: ["date_of_incorporation"], api: { getter: "LOS_GetDateOfIncorporation_069", setter: "LOS_SetDateOfIncorporation_070" } }},
  "LOS_GSTNumberInput_070": { component: GSTNumberInput, metadata: { id: "LOS_GSTNumberInput_070", name: "GST Number", icon: "Receipt", category: "Business Information", description: "The GSTIN of the business.", fields: ["gst_number"], api: { getter: "LOS_GetGSTNumber_025", setter: "LOS_SetGSTNumber_026" } }},
  "LOS_NatureOfBusinessInput_071": { component: NatureOfBusinessInput, metadata: { id: "LOS_NatureOfBusinessInput_071", name: "Nature of Business", icon: "Info", category: "Business Information", description: "The nature of the business.", fields: ["nature_of_business"], api: { getter: "LOS_GetNatureOfBusiness_032", setter: "LOS_SetNatureOfBusiness_033" } }},
  "LOS_AvgMonthlyBusinessIncomeInput_072": { component: AvgMonthlyBusinessIncomeInput, metadata: { id: "LOS_AvgMonthlyBusinessIncomeInput_072", name: "Avg Monthly Business Income", icon: "DollarSign", category: "Business Information", description: "Average monthly business income range.", fields: ["avg_monthly_business_income_range"], api: { getter: "LOS_GetMonthlyBusinessIncome_071", setter: "LOS_SetMonthlyBusinessIncome_072" } }},

  // --- Grouped Address Composites ---
  "LOS_BusinessAddressDetails_061": {
    component: BusinessAddressDetails,
    metadata: {
      id: "LOS_BusinessAddressDetails_061",
      name: "Business Address Details",
      icon: "Building2",
      category: "Address",
      description: "Business Address + Registered Address in one section. Includes a 'Same as Business Address' checkbox to auto-fill the Registered Address.",
      validation: {
        schema: z.object({
          business_address_line1: z.string().min(1, "Required"),
          business_address_line2: z.string().optional(),
          business_address_line3: z.string().min(1, "Required"),
          business_address_pincode: z.string().length(6, "Must be 6 digits"),
          business_address_landmark: z.string().optional(),
          registered_address_line1: z.string().min(1, "Required"),
          registered_address_line2: z.string().optional(),
          registered_address_line3: z.string().min(1, "Required"),
          registered_address_pincode: z.string().length(6, "Must be 6 digits"),
          registered_address_landmark: z.string().optional(),
        }),
        defaultValues: {},
      },
      api: { getter: "LOS_GetBusinessDetails_003", setter: "LOS_SetBusinessDetails_004" },
    }
  },

  "LOS_PersonalAddressDetails_084": {
    component: PersonalAddressDetails,
    metadata: {
      id: "LOS_PersonalAddressDetails_084",
      name: "Personal Address Details",
      icon: "Home",
      category: "Address",
      description: "Communication Address + Residential Address in one section. Includes a 'Same as Communication Address' checkbox to auto-fill the Residential Address.",
      validation: {
        schema: z.object({
          communication_address_line1: z.string().min(1, "Required"),
          communication_address_line2: z.string().optional(),
          communication_address_line3: z.string().min(1, "Required"),
          communication_address_pincode: z.string().length(6, "Must be 6 digits"),
          communication_address_landmark: z.string().optional(),
          residential_address_line1: z.string().min(1, "Required"),
          residential_address_line2: z.string().optional(),
          residential_address_line3: z.string().min(1, "Required"),
          residential_address_pincode: z.string().length(6, "Must be 6 digits"),
          residential_address_landmark: z.string().optional(),
        }),
        defaultValues: {},
      },
      fields: [
        "communication_address_line1",
        "communication_address_line2",
        "communication_address_line3",
        "communication_address_landmark",
        "communication_address_pincode",
        "residential_address_line1",
        "residential_address_line2",
        "residential_address_line3",
        "residential_address_landmark",
        "residential_address_pincode",
      ],
      api: { getter: "LOS_GetIndividualDetails_001", setter: "LOS_SetIndividualDetails_002" },
    }
  },

  // --- Niyogin Trigger Components ---
  "LOS_NiyoginBRE_306": {
    component: NiyoginBREComponent,
    metadata: {
      id: "LOS_NiyoginBRE_306",
      name: "Niyogin: BRE / Get Eligibility",
      icon: "BarChart2",
      category: "Niyogin",
      description: "Shows a button to trigger the Niyogin BRE (Get Eligibility) API. Displays CRIF score and max eligible amount, then advances the journey.",
      autoFire: true,
      api: { getter: "LOS_GetNiyoginBRE_306" },
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_NiyoginCreateCustomer_301": {
    component: NiyoginCreateCustomerComponent,
    metadata: {
      id: "LOS_NiyoginCreateCustomer_301",
      name: "Niyogin: Create Customer",
      icon: "UserPlus",
      category: "Niyogin",
      description: "Shows a button to trigger the Niyogin Create Customer API and advances the journey.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_NiyoginInitiateDigilocker_302": {
    component: NiyoginInitiateDigilockerComponent,
    metadata: {
      id: "LOS_NiyoginInitiateDigilocker_302",
      name: "Niyogin: Initiate DigiLocker",
      icon: "Link",
      category: "Niyogin",
      description: "Shows a button to start DigiLocker KYC. Redirects user to DigiLocker and handles the return callback automatically.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_NiyoginUpdateCustomer_303": {
    component: NiyoginUpdateCustomerComponent,
    metadata: {
      id: "LOS_NiyoginUpdateCustomer_303",
      name: "Niyogin: Update Customer",
      icon: "UserCheck",
      category: "Niyogin",
      description: "Shows a button to trigger the Niyogin Update Customer API and advances the journey.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_NiyoginCreateLoan_304": {
    component: NiyoginCreateLoanComponent,
    metadata: {
      id: "LOS_NiyoginCreateLoan_304",
      name: "Niyogin: Create Loan",
      icon: "FileText",
      category: "Niyogin",
      description: "Shows a button to trigger the Niyogin Create Loan API and advances the journey.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_NiyoginGetLoanStatus_305": {
    component: NiyoginGetLoanStatusComponent,
    metadata: {
      id: "LOS_NiyoginGetLoanStatus_305",
      name: "Niyogin: Get Loan Status",
      icon: "Activity",
      category: "Niyogin",
      description: "Shows a button to check the Niyogin loan status and displays the result before advancing the journey.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_NiyoginAAConsent_307": {
    component: NiyoginAAConsentComponent,
    metadata: {
      id: "LOS_NiyoginAAConsent_307",
      name: "Niyogin: AA Consent",
      icon: "Shield",
      category: "Niyogin",
      description: "Opens the Account Aggregator consent page in a new tab. User approves bank data sharing, then clicks 'I've Approved' to continue.",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_NiyoginAAStatus_308": {
    component: NiyoginAAStatusComponent,
    metadata: {
      id: "LOS_NiyoginAAStatus_308",
      name: "Niyogin: AA Status + BSA",
      icon: "BarChart2",
      category: "Niyogin",
      description: "Automatically polls consent status, submits FI request, waits for data, polls BSA status, and fetches the final BSA report. Shows a live progress view.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_NiyoginKFS_309": {
    component: NiyoginKFSComponent,
    metadata: {
      id: "LOS_NiyoginKFS_309",
      name: "Niyogin: KFS + Loan Agreement",
      icon: "FileText",
      category: "Niyogin",
      description: "Generates the Niyogin KFS + Loan Agreement PDF from the template, shows a PDF viewer link, and requires the user to accept before advancing.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_NiyoginAAFlow_309": {
    component: NiyoginAAFlowComponent,
    metadata: {
      id: "LOS_NiyoginAAFlow_309",
      name: "Niyogin: AA Consent + BSA Pipeline",
      icon: "Landmark",
      category: "Niyogin",
      description: "Combined component: initiates AA consent (opens new tab), polls for user approval in the background, then automatically runs the full BSA pipeline.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },

  // --- Caprion Components ---
  // Atomic components for login flow (IDs: 434-436)
  "LOS_MobileOTPLogin_434": {
    component: MobileOTPLogin,
    metadata: {
      id: "LOS_MobileOTPLogin_434",
      name: "Mobile OTP Login",
      icon: "Smartphone",
      category: "Caprion",
      description: "Combined mobile number input and OTP verification flow",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_TermsConsentCheckbox_435": {
    component: TermsConsentCheckbox,
    metadata: {
      id: "LOS_TermsConsentCheckbox_435",
      name: "Terms & Privacy Consent",
      icon: "FileText",
      category: "Caprion",
      description: "Consent checkbox for Terms & Conditions and Privacy Policy",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_PerfiosConsentCheckbox_436": {
    component: PerfiosConsentCheckbox,
    metadata: {
      id: "LOS_PerfiosConsentCheckbox_436",
      name: "Perfios KYC Consent",
      icon: "CheckSquare",
      category: "Caprion",
      description: "Consent checkbox for Perfios KYC data fetch",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionLanding_500": {
    component: Landing,
    metadata: {
      id: "LOS_CaprionLanding_500",
      name: "Non-Captive: Landing Page",
      icon: "Home",
      category: "Caprion",
      description: "Landing page for Caprion journey with features and Apply Now button. Has selfAdvances button.",
      selfAdvances: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionConsent_501": {
    component: Consent,
    metadata: {
      id: "LOS_CaprionConsent_501",
      name: "Non-Captive: Consent Page",
      icon: "FileText",
      category: "Caprion",
      description: "Consent page for Perfios KYC authorization. Saves consent to database and has selfAdvances button.",
      fields: ["perfios_consent"],
      selfAdvances: true,
      api: {
        getter: "LOS_GetPerfiosConsent_501",
        setter: "LOS_SetPerfiosConsent_502"
      },
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionLenderSelection_400": {
    component: LenderSelection,
    metadata: {
      id: "LOS_CaprionLenderSelection_400",
      name: "Non-Captive: Lender Selection",
      icon: "Building2",
      category: "Caprion",
      description: "Select between Caprion and other lenders. Shows lender cards with features and allows user to choose.",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  // Removed unused Caprion components (superseded by atomic components):
  // - LOS_CaprionLenderSelection_400 (duplicate)
  // - LOS_CaprionLeadForm_401 (superseded by atomic components)
  // - LOS_CaprionPersonalInfo_408 (superseded by atomic components)
  // - LOS_CaprionAddressInfo_409 (superseded by atomic components)
  // - LOS_CaprionProfessionalInfo_410 (superseded by atomic components)
  // - LOS_CaprionLoanDetails_411 (superseded by atomic components)
  
  "LOS_CaprionStatusCheck_402": {
    component: CaprionStatusCheck,
    metadata: {
      id: "LOS_CaprionStatusCheck_402",
      name: "Caprion: Status Check",
      description: "Check Caprion loan application status",
      category: "Caprion",
      getterApi: "LOS_GetCaprionStatus_403",
      setterApi: "LOS_SetCaprionStatus_404"
    }
  },

  "LOS_CaprionFinalOffer_445": {
    component: CaprionFinalOffer,
    metadata: {
      id: "LOS_CaprionFinalOffer_445",
      name: "Caprion: Final Offer Display",
      icon: "Activity",
      category: "Caprion",
      description: "Check the current status of a Caprion loan application by Application ID.",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionCKYC_403": {
    component: CaprionAadhaarVerification,
    metadata: {
      id: "LOS_CaprionCKYC_403",
      name: "Caprion: Aadhaar Verification",
      icon: "Shield",
      category: "Caprion",
      description: "Verify identity using Aadhaar via Digilocker or manual upload.",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionDocuments_404": {
    component: CaprionDocuments,
    metadata: {
      id: "LOS_CaprionDocuments_404",
      name: "Caprion: Document Upload",
      icon: "Upload",
      category: "Caprion",
      description: "Upload required documents (PAN, Aadhaar, Bank Statement, etc.) for loan application.",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_BankAccountNumberInput_429": {
    component: BankAccountNumberInput,
    metadata: {
      id: "LOS_BankAccountNumberInput_429",
      name: "Bank Account Number Input",
      icon: "CreditCard",
      category: "Bank Details",
      description: "Atomic component for bank account number input (9-18 digits).",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
      api: {
        getter: "LOS_GetBankAccountNumber_423",
        setter: "LOS_SetBankAccountNumber_424"
      },
    }
  },
  "LOS_IFSCCodeInput_430": {
    component: IFSCCodeInput,
    metadata: {
      id: "LOS_IFSCCodeInput_430",
      name: "IFSC Code Input",
      icon: "Building2",
      category: "Bank Details",
      description: "Atomic component for IFSC code input with auto-uppercase formatting.",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
      api: {
        getter: "LOS_GetIFSCCode_425",
        setter: "LOS_SetIFSCCode_426"
      },
    }
  },
  "LOS_AccountTypeInput_431": {
    component: AccountTypeInput,
    metadata: {
      id: "LOS_AccountTypeInput_431",
      name: "Account Type Input",
      icon: "Wallet",
      category: "Bank Details",
      description: "Atomic component for account type selection (Savings/Current).",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
      api: {
        getter: "LOS_GetAccountType_427",
        setter: "LOS_SetAccountType_428"
      },
    }
  },
  "LOS_BankConsentsInput_432": {
    component: BankConsentsInput,
    metadata: {
      id: "LOS_BankConsentsInput_432",
      name: "Bank Consents Input",
      icon: "CheckSquare",
      category: "Bank Details",
      description: "Atomic component for bank-related consent checkboxes (balance promise, credit review).",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
      api: {
        getter: "LOS_GetBankConsents_421",
        setter: "LOS_SetBankConsents_422"
      },
    }
  },
  "LOS_RepaymentMethodInput_433": {
    component: RepaymentMethodInput,
    metadata: {
      id: "LOS_RepaymentMethodInput_433",
      name: "Repayment Method Input",
      icon: "Calendar",
      category: "Loan Details",
      description: "Atomic component for selecting repayment method (fixed installment or percentage of revenue).",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
      api: {
        getter: "LOS_GetRepaymentMethod_434",
        setter: "LOS_SetRepaymentMethod_435"
      },
    }
  },
  "LOS_RepaymentFrequencyInput_436": {
    component: RepaymentFrequencyInput,
    metadata: {
      id: "LOS_RepaymentFrequencyInput_436",
      name: "Repayment Frequency Input",
      icon: "Calendar",
      category: "Loan Details",
      description: "Atomic component for selecting repayment frequency (EMI/EDI/EWI).",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
      api: {
        getter: "LOS_GetRepaymentFrequency_437",
        setter: "LOS_SetRepaymentFrequency_438"
      },
    }
  },
  "LOS_SmartLoanTenureInput_439": {
    component: SmartLoanTenureInput,
    metadata: {
      id: "LOS_SmartLoanTenureInput_439",
      name: "Smart Loan Tenure Input",
      icon: "Clock",
      category: "Loan Details",
      description: "Smart tenure component that auto-adjusts display based on repayment frequency (days/weeks/months).",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
      api: {
        getter: "LOS_GetLoanTenure_440",
        setter: "LOS_SetLoanTenure_441"
      },
    }
  },
  "LOS_ViewKFSButton_442": {
    component: ViewKFSButton,
    metadata: {
      id: "LOS_ViewKFSButton_442",
      name: "View KFS Button",
      icon: "FileText",
      category: "Actions",
      description: "Generic button to view Key Fact Statement (KFS). Can be placed anywhere in the journey.",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionAgreementPdf_415": {
    component: CaprionAgreementPdf,
    metadata: {
      id: "LOS_CaprionAgreementPdf_415",
      name: "Caprion: Agreement PDF Display",
      icon: "FileText",
      category: "Caprion",
      description: "Display loan agreement PDF for review before signing. User must confirm reading before proceeding.",
      autoFire: false,
      selfAdvances: true,  // Component has its own Continue button
      fullPageComponent: true,  // Renders own min-h-screen layout — JourneyPage suppresses its header
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionESign_416": {
    component: CaprionESign,
    metadata: {
      id: "LOS_CaprionESign_416",
      name: "Caprion: Agreement Signing (OTP)",
      icon: "FileSignature",
      category: "Caprion",
      description: "OTP-based agreement signing. Sends OTP to mobile, user enters OTP to complete signing.",
      autoFire: false,
      selfAdvances: true,  // Component automatically advances after OTP verification
      fullPageComponent: true,  // Renders own min-h-screen layout — JourneyPage suppresses its header
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionDealerApproval_446": {
    component: CaprionDealerApproval,
    metadata: {
      id: "LOS_CaprionDealerApproval_446",
      name: "Caprion: Dealer Approval Validation",
      icon: "CheckCircle2",
      category: "Caprion",
      description: "Validates loan details with Caprion and approves for disbursal. Fetches dealer approval details, validates against our loan data, and calls DealerApprovalForDisbursal if validation passes.",
      autoFire: true,  // Auto-triggers validation on mount
      fullPageComponent: true,  // Renders own min-h-screen layout — JourneyPage suppresses its header
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionAgreementStatus_417": {
    component: CaprionAgreementStatus,
    metadata: {
      id: "LOS_CaprionAgreementStatus_417",
      name: "Caprion: Agreement Status",
      icon: "CheckCircle",
      category: "Caprion",
      description: "Shows agreement signing status from Caprion. Auto-fetches status and displays result.",
      autoFire: true,  // Auto-fetches status on mount
      fullPageComponent: true,  // Renders own min-h-screen layout — JourneyPage suppresses its header
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionAAConsent_420": {
    component: CaprionAAConsent,
    metadata: {
      id: "LOS_CaprionAAConsent_420",
      name: "Caprion: AA Consent (Ignosis)",
      icon: "Landmark",
      category: "Caprion",
      description: "Shows consent checkbox + instructions for Ignosis Account Aggregator. On submit, calls POST /api/caprion/aa/initiate/ and opens the Ignosis AA window for the customer to select their bank and approve data sharing. User clicks 'I've Completed' to advance to AA Status page.",
      autoFire: true,   // Component drives journey via onDone — must be true so JourneyPage wires the onDone callback
      fullPageComponent: true,  // Renders own min-h-screen layout — JourneyPage suppresses its header
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionAAStatus_421": {
    component: CaprionAAStatus,
    metadata: {
      id: "LOS_CaprionAAStatus_421",
      name: "Caprion: AA Status (Ignosis)",
      icon: "BarChart2",
      category: "Caprion",
      description: "Polls GET /api/caprion/aa/status/ every 4 seconds. Shows animated loading screen while Ignosis fetches bank data. Advances automatically when status is SUCCESS.",
      autoFire: true,
      fullPageComponent: true,  // Renders own min-h-screen layout — JourneyPage suppresses its header
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionAAConcentFlow_422": {
    component: CaprionAAConcentFlow,
    metadata: {
      id: "LOS_CaprionAAConcentFlow_422",
      name: "Caprion: AA Consent Flow",
      icon: "Landmark",
      category: "Caprion",
      description:
        "Full 3-step Caprion AA Concent flow: (1) Fetches FIP bank list from " +
        "/api/caprion/aa/concent/fetch-banks/ and renders a multi-select dropdown. " +
        "(2) On submit, calls /api/caprion/aa/concent/create/ with selected fip_ids as " +
        "bank_codes array, then redirects user to the Ignosis AA portal (redirectionUrl). " +
        "(3) After user returns and clicks 'I've Completed', polls " +
        "/api/caprion/aa/concent/status/ every 5 s until status is ACTIVE/COMPLETED, " +
        "then calls onDone() to advance the journey.",
      autoFire: true,
      fullPageComponent: true,  // Renders own min-h-screen layout — JourneyPage suppresses its header
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionLoanVerification_423": {
    component: CaprionLoanVerification,
    metadata: {
      id: "LOS_CaprionLoanVerification_423",
      name: "Caprion: Loan Verification",
      icon: "CheckCircle",
      category: "Caprion",
      description:
        "Dedicated page for loan status polling after AA consent. Automatically starts " +
        "polling /api/caprion/start-status-poll/ on mount, then polls /api/caprion/poll-status/ " +
        "every 5 seconds for up to 10 minutes (120 attempts). Handles 4 statuses: Pending (continue), " +
        "Approved (proceed to next page), Rejected/Withdrawn (show rejection screen and block navigation). " +
        "Backend automatically updates LoanDetail with approved values. Shows animated loader with progress bar.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_CaprionBureauVerification_447": {
    component: BureauVerification,
    metadata: {
      id: "LOS_CaprionBureauVerification_447",
      name: "Caprion: Bureau Verification",
      icon: "Shield",
      category: "Caprion",
      description:
        "Dedicated page for bureau status polling after lender selection. Automatically starts " +
        "polling /api/caprion/start-status-poll/ with bureau_context on mount, then polls " +
        "/api/caprion/poll-status/ every 5 seconds for up to 10 minutes (120 attempts). " +
        "Handles terminal statuses: Pending/Accepted/Approved (proceed to next page), " +
        "Rejected/Withdrawn (show rejection screen and block navigation). Shows animated loader " +
        "with bureau verification progress steps.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_SelfiePhotoCapture_412": {
    component: SelfiePhotoCapture,
    metadata: {
      id: "LOS_SelfiePhotoCapture_412",
      name: "Selfie Photo Capture",
      icon: "Camera",
      category: "Verification",
      description: "Capture selfie photo with front camera and GPS location for identity verification.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_ShopPhotoCapture_413": {
    component: ShopPhotoCapture,
    metadata: {
      id: "LOS_ShopPhotoCapture_413",
      name: "Shop Photo Capture",
      icon: "Camera",
      category: "Verification",
      description: "Capture shop photo with back camera (or front if unavailable) and GPS location for business verification.",
      autoFire: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_DigioMandateSetup_414": {
    component: DigioMandateSetup,
    metadata: {
      id: "LOS_DigioMandateSetup_414",
      name: "Digio eMandate Setup",
      icon: "CreditCard",
      category: "Mandate",
      description: "Setup UPI Autopay and NACH mandates via Digio for automatic loan repayments. Sequential flow: UPI first, then NACH.",
      autoFire: false,
      // selfAdvances: true — SetupMandates manages its own navigation (popup flow + navigate()).
      // This hides the generic JourneyPage footer "Continue" button which would otherwise
      // let the user bypass mandate completion entirely.
      selfAdvances: true,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_UPIMandate_418": {
    component: UPIMandate,
    metadata: {
      id: "LOS_UPIMandate_418",
      name: "UPI Mandate (Caprion)",
      icon: "Smartphone",
      category: "Mandate",
      description: "UPI Autopay mandate setup with Digio integration. Shows completion state on page refresh. Fires onChange({completed:true}) on success so JourneyPage enables the Continue button. NOTE: the Caprion SaveMandateDetails push is NOT done here — it runs once from Continue via /api/caprion/mandates/save/ (see backend api_views/caprion/mandate_save.py).",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_NACHMandate_419": {
    component: NACHMandate,
    metadata: {
      id: "LOS_NACHMandate_419",
      name: "NACH Mandate (Caprion)",
      icon: "Building2",
      category: "Mandate",
      description: "NACH mandate setup with Digio integration. Disabled until UPI is completed. Shows completion state on page refresh. Fires onChange({completed:true}) on success so JourneyPage enables the Continue button. NOTE: the Caprion SaveMandateDetails push is NOT done here — it runs once from Continue via /api/caprion/mandates/save/ (see backend api_views/caprion/mandate_save.py).",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  "LOS_BankDetailsWrapper_435": {
    component: BankDetailsWrapper,
    metadata: {
      id: "LOS_BankDetailsWrapper_435",
      name: "Bank Details (Full Page)",
      icon: "Building2",
      category: "Bank Details",
      description: "Wrapper around BankAccountAuthentication page. Collects bank account number, IFSC code, account type, and consent checkboxes. Includes penny drop verification.",
      autoFire: false,
      validation: { schema: undefined, defaultValues: {} },
    }
  },
  // --- Text / Content Components ---
  "LOS_TextBlock_100": {
    component: TextBlock,
    metadata: {
      id: "LOS_TextBlock_100",
      name: "Text Block",
      icon: "FileText",
      category: "Content",
      description: "A rich-text block with an editable heading and WYSIWYG body. Content is stored as HTML in PageConfig.layout.",
      validation: {
        schema: undefined as any,
        defaultValues: { heading: "", headingHtml: "", content: "" },
      },
    },
  },

  // --- Image Block ---
  "LOS_ImageBlock_101": {
    component: ImageBlock,
    metadata: {
      id: "LOS_ImageBlock_101",
      name: "Image",
      icon: "Image",
      category: "Content",
      description: "Displays an image from a URL with configurable width, alignment, and corner radius. Stored in PageConfig.layout.",
      validation: {
        schema: undefined as any,
        defaultValues: { src: "", alt: "", width: "100%", align: "center", borderRadius: "0px" },
      },
    },
  },

  // --- Progress Bar ---
  "LOS_ProgressBar_102": {
    component: ProgressBar,
    metadata: {
      id: "LOS_ProgressBar_102",
      name: "Progress Bar",
      icon: "BarChart2",
      category: "Content",
      description: "Shows the user's progress through the journey. Supports bar, dots, and steps styles.",
      validation: {
        schema: undefined as any,
        defaultValues: { label: "", color: "#16a34a", showStepCount: true, style: "bar" },
      },
    },
  },

  // --- Segmented Progress Bar ---
  "LOS_SegmentedProgressBar_103": {
    component: SegmentedProgressBar,
    metadata: {
      id: "LOS_SegmentedProgressBar_103",
      name: "Segmented Progress Bar",
      icon: "BarChart3",
      category: "Content",
      description: "Shows journey progress with segmented bars that dynamically adjust based on total pages. Features step badge and percentage display.",
      validation: {
        schema: undefined as any,
        defaultValues: { color: "#065a4e", showPercentage: true, showStepBadge: true },
      },
    },
  },

  // --- Udhyam Fetch ---
  "LOS_UdhyamFetch_310": {
    component: UdhyamFetch,
    metadata: {
      id: "LOS_UdhyamFetch_310",
      name: "Udhyam Fetch",
      icon: "Search",
      category: "Business Information",
      description:
        "Auto-triggers a background Perfios mobile-to-Udhyam lookup on mount. " +
        "Stores the full API response in BusinessDetail.udhyam_details (JSON) and " +
        "populates BusinessDetail.udyam_number with the first URN found. " +
        "Non-blocking — advances the journey even if no Udhyam record is found.",
      autoFire: true,
      selfAdvances: true,
      validation: { schema: undefined as any, defaultValues: {} },
    },
  },

  // --- Dynamic Form Builder (Frappe-like) ---
  "LOS_DynamicFormBuilder_400": {
    component: DynamicFormBuilder,
    metadata: {
      id: "LOS_DynamicFormBuilder_400",
      name: "Dynamic Form Builder",
      icon: "FormInput",
      category: "Form Builder",
      description:
        "A Frappe-like generic form builder that allows creating custom forms at runtime. " +
        "Configure field types (text, number, email, select, etc.), labels, backend field names, " +
        "validation rules, and options directly in the Journey Builder. " +
        "Perfect for creating custom data collection forms without coding.",
      validation: {
        schema: undefined as any,
        defaultValues: { __fields: [] },
      },
    },
  },

};

export type ComponentKey = keyof typeof componentRegistry;

/**
 * Resolves a registry entry by component ID.
 * Returns the full RegistryEntry object or undefined if not found.
 */
export function resolveRegistryEntry(componentId: string): RegistryEntry | undefined {
  return componentRegistry[componentId as ComponentKey];
}

export default componentRegistry;

